import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { Textinput as HrTextInput, TextinputProps } from "@yandex-int/hr-components/Textinput/bundle";
import { useSharedTranslations } from "../../lib/i18n";
import { IValidateError } from "../../lib";
import styles from "./textinput.module.scss";

interface Props extends TextinputProps {
    validate?: boolean;
    containerClassName?: string;
    validateFunc?: (value: string) => IValidateError;
    onValidate?: (error: IValidateError) => void;
    triggerValidate?: number;
}

export const Textinput: React.FC<Props> = ({
                                               hint,
                                               state,
                                               containerClassName,
                                               validate,
                                               validateFunc,
                                               onValidate,
                                               triggerValidate,
                                               ...props
                                           }) => {
    const t = useSharedTranslations();
    const { i18n } = useTranslation();
    const [value, setValue] = useState<string>("");
    const [isError, setIsError] = useState(false);
    const [validationError, setValidationError] = useState<string>();

    useEffect(() => {
        if ((triggerValidate || !!value) && validate) {
            validateValue(value);
        } else {
            setIsError(false);
            setValidationError(undefined);
        }
    }, [triggerValidate, validate, validateFunc, value]);

    useEffect(() => {
        if (props.value !== value) {
            setValue(props.value as string);
        }
    }, [props.value]);

    useEffect(() => {
        if (isError) {
            validateValue(value);
        }
    }, [i18n.language]);

    const getState = (value?: boolean) => !value ? undefined : "error";

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const changeVal = e.target.value;
        setValue(changeVal);
        if (validate) {
            validateValue(changeVal);
        }

        props.onChange && props.onChange(e);
    };

    const validateValue = (val: string) => {
        let error = false;
        let message: string | undefined = undefined;
        if (!validateFunc) {
            if (!val) {
                error = true;
                message = t("requiredFieldError");
            }
        } else {
            const validation = validateFunc(val);
            error = !validation.valid;
            message = validation.message;
        }
        setIsError(error);
        setValidationError(message);
        onValidate && onValidate({ valid: !error, message: message });
    };

    return (
        <div className={cn(styles.textInput, containerClassName)}>
            <HrTextInput
                hint={validationError ?? hint}
                state={state ? state : getState(isError)}
                {...props}
                onChange={onChange}
            />
        </div>
    );
};
